/**
 * MKS-339 — TanStack Query hooks for the helpdesk.
 *
 * Query-key convention:
 *   ['helpdesk', 'requests', filters]  — the inbox, keyed by the filter set
 *                                        so changing a filter is a new fetch
 *                                        and going back is a cache hit
 *   ['helpdesk', 'summary']            — the dashboard card
 *   ['helpdesk', 'filter-options']     — the picker contents
 *   ['requestCategories', projectId]   — per project (same shape as
 *                                        ['projectSlaTiers', projectId])
 *   ['cannedReplies', projectId|'all'] — per scope
 *
 * Every triage mutation invalidates the whole `['helpdesk']` subtree: a change
 * of owner, tier or category can move a row in or out of the current filter,
 * and it changes the summary counts too, so a narrower invalidation would
 * leave a stale row on screen.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchHelpdeskRequests,
  fetchHelpdeskSummary,
  fetchHelpdeskFilterOptions,
  fetchRequestCategories,
  fetchExternalRequestCategories,
  createRequestCategory,
  updateRequestCategory,
  reorderRequestCategories,
  deleteRequestCategory,
  fetchCannedReplies,
  fetchAllCannedReplies,
  createCannedReply,
  updateCannedReply,
  deleteCannedReply,
  setTaskCategory,
  type HelpdeskFilters,
} from '../services/api/helpdesk';
import { moveTaskToOpen, patchTaskTier } from '../services/api/board';
import { patchTaskAssignee } from '../services/api/tasks';

// ---------------------------------------------------------------------------
// The inbox
// ---------------------------------------------------------------------------

export function useHelpdeskRequests(filters: HelpdeskFilters, enabled = true) {
  return useQuery({
    queryKey: ['helpdesk', 'requests', filters],
    queryFn: () => fetchHelpdeskRequests(filters).then((r) => r.data),
    enabled,
  });
}

export function useHelpdeskSummary(enabled = true) {
  return useQuery({
    queryKey: ['helpdesk', 'summary'],
    queryFn: () => fetchHelpdeskSummary().then((r) => r.data),
    enabled,
  });
}

export function useHelpdeskFilterOptions(enabled = true) {
  return useQuery({
    queryKey: ['helpdesk', 'filter-options'],
    queryFn: () => fetchHelpdeskFilterOptions().then((r) => r.data),
    enabled,
  });
}

// ---------------------------------------------------------------------------
// Triage from the inbox
// ---------------------------------------------------------------------------

export function useSetTaskCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, categoryId }: { taskId: number; categoryId: number | null }) =>
      setTaskCategory(taskId, categoryId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['helpdesk'] }),
  });
}

/**
 * Owner and tier from the inbox reuse the EXISTING endpoints
 * (`PATCH /api/tasks/:id/assignee`, `PATCH /api/tasks/:id/tier`) — the inbox
 * adds no second way to do either. These wrappers exist only to attach the
 * helpdesk invalidation; the board has its own optimistic hooks
 * (`useUpdateTaskTier`) which patch the board cache and are not usable here
 * because the inbox is not a board cache.
 */
export function useSetRequestOwner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, ownerId }: { taskId: number; ownerId: number | null }) =>
      patchTaskAssignee(taskId, ownerId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['helpdesk'] }),
  });
}

export function useSetRequestTier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, tierId }: { taskId: number; tierId: number }) =>
      patchTaskTier(taskId, tierId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['helpdesk'] }),
  });
}

/**
 * "Move to Open" — the Phase 51 intake transition, unchanged. Wrapped here
 * only so the inbox's invalidation happens alongside the board's.
 */
export function useMoveRequestToOpen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: number) => moveTaskToOpen(taskId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['helpdesk'] });
      qc.invalidateQueries({ queryKey: ['board'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Request categories
// ---------------------------------------------------------------------------

export function useRequestCategories(
  projectId: number | undefined,
  includeArchived = false,
) {
  return useQuery({
    queryKey: ['requestCategories', projectId, includeArchived],
    queryFn: () => fetchRequestCategories(projectId!, includeArchived).then((r) => r.data),
    enabled: !!projectId,
  });
}

/**
 * The portal's copy. `retry: false` and a swallowed error on purpose: the
 * category field is OPTIONAL, so a project with the helpdesk flag off (the
 * endpoint 404s), or any other failure, must leave the client able to submit
 * a request without one rather than blocking the form.
 */
export function useExternalRequestCategories(projectId: string | undefined) {
  return useQuery({
    queryKey: ['externalRequestCategories', projectId],
    queryFn: () =>
      fetchExternalRequestCategories(projectId!)
        .then((r) => r.data)
        .catch(() => []),
    enabled: !!projectId,
    retry: false,
  });
}

export function useCreateRequestCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      input,
    }: {
      projectId: number;
      input: { name: string; orderIndex?: number };
    }) => createRequestCategory(projectId, input),
    onSuccess: (_d, { projectId }) => {
      qc.invalidateQueries({ queryKey: ['requestCategories', projectId] });
      // The inbox's category filter lists categories too.
      qc.invalidateQueries({ queryKey: ['helpdesk', 'filter-options'] });
    },
  });
}

export function useUpdateRequestCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      categoryId,
      input,
    }: {
      projectId: number;
      categoryId: number;
      input: { name?: string; orderIndex?: number; archived?: boolean };
    }) => updateRequestCategory(projectId, categoryId, input),
    onSuccess: (_d, { projectId }) => {
      qc.invalidateQueries({ queryKey: ['requestCategories', projectId] });
      qc.invalidateQueries({ queryKey: ['helpdesk'] });
    },
  });
}

/**
 * Reordering is ONE mutation carrying the whole order, not N `useUpdate...`
 * calls: the server renormalises the list in a single transaction, so a move
 * either lands or does not.
 */
export function useReorderRequestCategories() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      categoryIds,
    }: {
      projectId: number;
      categoryIds: number[];
    }) => reorderRequestCategories(projectId, categoryIds),
    onSuccess: (_d, { projectId }) => {
      qc.invalidateQueries({ queryKey: ['requestCategories', projectId] });
      // The inbox's category filter and triage picker list them in this order.
      qc.invalidateQueries({ queryKey: ['helpdesk'] });
    },
  });
}

export function useDeleteRequestCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, categoryId }: { projectId: number; categoryId: number }) =>
      deleteRequestCategory(projectId, categoryId),
    onSuccess: (_d, { projectId }) => {
      qc.invalidateQueries({ queryKey: ['requestCategories', projectId] });
      qc.invalidateQueries({ queryKey: ['helpdesk'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Canned replies
// ---------------------------------------------------------------------------

export function useCannedReplies(projectId?: number | null, enabled = true) {
  return useQuery({
    queryKey: ['cannedReplies', projectId ?? null],
    queryFn: () => fetchCannedReplies(projectId).then((r) => r.data),
    enabled,
  });
}

export function useAllCannedReplies(enabled = true) {
  return useQuery({
    queryKey: ['cannedReplies', 'all'],
    queryFn: () => fetchAllCannedReplies().then((r) => r.data),
    enabled,
  });
}

export function useCreateCannedReply() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { projectId?: number | null; title: string; body: string }) =>
      createCannedReply(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cannedReplies'] }),
  });
}

export function useUpdateCannedReply() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      replyId,
      input,
    }: {
      replyId: number;
      input: { title?: string; body?: string };
    }) => updateCannedReply(replyId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cannedReplies'] }),
  });
}

export function useDeleteCannedReply() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (replyId: number) => deleteCannedReply(replyId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cannedReplies'] }),
  });
}
